import React from 'react';

// "Ledger & Seal" family, Personalized Content & Product Discovery edition
// -- shares the shell used by DynamicPricingOnePager, RFPResponseOnePager,
// and AgenticReadinessOnePager. No title/video here -- heroBanner: true
// handles that (see useCases.js), with VideoPlaceholder standing in for the
// video until a real walkthrough exists.
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  violetLight: '#a78bfa',
};

const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 35%, ${PALETTE.indigo} 65%, ${PALETTE.violet} 85%, ${PALETTE.violetLight} 100%)`;

const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const checklist = [
  { title: 'Real-time signal capture', body: 'Kinesis and Lambda capture clicks, add-to-carts, and smart cart telemetry.' },
  { title: 'Managed retail recommendations', body: 'Amazon Personalize handles cold-start users and adjusts feeds as a customer shops.' },
  { title: 'Millisecond profile lookups', body: 'DynamoDB holds profiles, dietary rules, and purchase cycles at single-digit ms.' },
  { title: 'Semantic intent search', body: 'Understands "taco night" and fetches tortillas, seasoning, and ground beef.' },
  { title: 'Autonomous meal planning', body: 'Agents on Amazon Bedrock handle recipes, inventory, budget, and cart in one pass.' },
  { title: 'Hyper-personalized content', body: 'Agents generate custom image layouts and copy per shopper, not a generic push.' },
];

const stamps = [
  { num: '01', title: 'Signals arrive', body: 'Clicks, add-to-carts, and smart cart telemetry stream in' },
  { num: '02', title: 'Profiles unify', body: 'POS, mobile, and loyalty data merge into one customer profile' },
  { num: '03', title: 'The agent reasons', body: 'Intent, dietary rules, budget, and local inventory together' },
  { num: '04', title: 'Experience delivered', body: 'A personalized feed, recipe bundle, or filled cart' },
];

const ledgerRows = [
  { label: 'Revenue lift when adopted correctly', value: '15%' },
  { label: 'Reduction in acquisition cost', value: '50%' },
  { label: 'Defection from generic experiences, prevented', value: '62%' },
  { label: 'Conversion lift at Petco via intelligent search', value: '13%' },
];

const PersonalizedContentDiscoveryOnePager = () => {
  return (
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-5">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm sm:mb-6 sm:p-5 sm:pl-6"
        style={{ borderLeft: `5px solid ${PALETTE.blue}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">Rigid keyword search that ignores the shopper in front of it.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Search on most grocery sites is just word-matching: type "milk," get dairy, with no idea who's asking or what they actually need. That leaves shoppers falling back on the same handful of staples every week, so baskets stay small and premium items sit on the shelf undiscovered. Without a way to show shoppers something more relevant, the only tool left to grow sales is a discount, which eats into margin instead of building loyalty.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">Agents plan and execute the whole shop, not just the search.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            The system learns each shopper's habits, dietary needs, and budget, then uses that to recommend the right products instead of just matching a search term. A request like "a gluten-free meal plan for a family of four under $150" comes back as a full recipe, checked against local stock, with the cart already filled. The result is bigger baskets built on relevance and discovery, not discounts, and every preference it remembers makes it harder for a shopper to switch to a competitor.
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">What the solution does</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:mb-6">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 sm:px-4 sm:py-3 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.indigo}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">How it works</h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7 sm:mb-6 sm:p-5">
        <div
          className="absolute left-[6%] right-[6%] top-[51px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-4">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{
                  background: `linear-gradient(135deg, ${
                    [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet][i]
                  }, ${[PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violetLight][i]})`,
                }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
        <p className="relative mt-6 border-t border-b-0 border-l-0 border-r-0 border-dashed border-[#e7dcc8] pt-4 text-base italic text-gray-500">
          Foundation models run through Amazon Bedrock so proprietary transaction data never leaks, and dietary and allergy rules held in DynamoDB bound every recommendation.
        </p>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900 sm:mb-3">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:grid-cols-4">
        {ledgerRows.map((row, i) => (
          <div
            key={row.label}
            className={`flex flex-col items-center gap-1 px-4 py-5 text-center ${
              i !== ledgerRows.length - 1 ? 'border-b border-[#f1e9d8] sm:border-b-0 sm:border-r' : ''
            }`}
          >
            <span
              className="text-4xl font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[i + 1]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {row.value}
            </span>
            <span className="text-base font-semibold text-gray-600">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PersonalizedContentDiscoveryOnePager;
